// components\ModalMeasure.tsx
// components/ModalMeasure.tsx
import React, { useEffect, useState } from 'react';
import './ModalLed.css';
import MQTTService from './MQTTService';
import jwt_decode from 'jwt-decode';
import { fetchRecordsByEmail } from './DataHandler';

interface DeviceRecord {
  mac: string;
  name?: string[];  // name[0]=장치명, 이후는 in/out 라벨
  in?: number[];
  out?: number[];
}

interface ModalMeasureProps {
  onCancel?: () => void;
  mqttMessage: string;   // MQTT 수신 원문
  deviceMac: string;     // 센서가 달린 내 장치 MAC
  //type: string;          // device 종류
  typeSensor: string;    // 'humidity' | 'temperature' | 'light' ...
  label: string;         // UI 라벨(예: '습도 (%)')
  value?: any;
}

type TriggerKind = 'up' | 'down' | '';

type LogRow = {
  slotIndex: number;  // 트리거 조건의 고유 인덱스
  trigger: 'up' | 'down';  // 상승(⬆️) 또는 하강(⬇️) 트리거
  typeSensor: string;  // 트리거가 발생한 센서 타입 (예: 'humidity', 'temperature' 등)
  triggerValue?: number;  // 트리거가 동작하는 센서 값 (optional)
  duration?: number;   // 동작 시간 (선택 사항)
  delay?: number;      // 지연 시간 (선택 사항)
  mac: string;         // 동작할 타겟 장치 MAC 주소
  portNo: number;      // 동작할 포트 번호
  value: boolean;      // ON/OFF 상태
};

const ModalMeasure: React.FC<ModalMeasureProps> = ({
  onCancel, mqttMessage, deviceMac, typeSensor, label,
}) => {
  const [email, setEmail] = useState<string | null>(null);
  const [records, setRecords] = useState<DeviceRecord[]>([]);
  const [rows, setRows] = useState<LogRow[]>([]);

  // ✅ 트리거를 Select로 한 개만 선택 (기본: 미선택)
  const [selectedTrigger, setSelectedTrigger] = useState<TriggerKind>('');

  // ⬆️ 올라갈 때 입력값
  const [thresholdUp, setThresholdUp] = useState<number | ''>('');
  const [durationUp, setDurationUp] = useState<number | ''>('');
  const [delayUp, setDelayUp] = useState<number | ''>(''); // 추가
  const [upDeviceMac, setUpDeviceMac] = useState<string>('');
  const [upPort, setUpPort] = useState<number | null>(null);
  const [upState, setUpState] = useState<boolean | null>(null);

  // ⬇️ 내려갈 때 입력값
  const [thresholdDown, setThresholdDown] = useState<number | ''>('');
  const [durationDown, setDurationDown] = useState<number | ''>('');
  const [delayDown, setDelayDown] = useState<number | ''>(''); // 추가
  const [downDeviceMac, setDownDeviceMac] = useState<string>('');
  const [downPort, setDownPort] = useState<number | null>(null);
  const [downState, setDownState] = useState<boolean | null>(null);

  // 센서 보정
  const [calSensor, setCalSensor] = useState<number | ''>('');

  // ===== MQTT Helper =====
  const topicIn = () => `i2r/${email}/in`;
  const publish = (payload: any) =>
    MQTTService.getInstance().publish(topicIn(), JSON.stringify(payload));

  // ===== 초기화 =====
  const resetInputs = () => {
    setThresholdUp(''); setDurationUp(''); setDelayUp(''); setUpDeviceMac(''); setUpPort(null); setUpState(null);
    setThresholdDown(''); setDurationDown(''); setDelayDown(''); setDownDeviceMac(''); setDownPort(null); setDownState(null);
    setCalSensor('');
    setSelectedTrigger('');
  };

  // ===== 사용자 이메일 추출 =====
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const decoded: any = jwt_decode(token);
      setEmail(decoded.email);
    } catch (e) {
      console.error('토큰 디코딩 오류:', e);
    }
  }, []);

  // ===== 내 이메일의 기기 목록 =====
  useEffect(() => {
    if (!email) return;
    fetchRecordsByEmail(email)
      .then((data) => setRecords(data))
      .catch((err) => console.error('기기 목록 가져오기 실패:', err));
  }, [email]);

  // ===== 최초/변경시 목록 보기 =====
  useEffect(() => {
    if (email && deviceMac && typeSensor) handleList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, deviceMac, typeSensor]);

  // ===== LIST 응답 파싱 (신/구 포맷 수용 + trigger 다양형 인식) =====

  useEffect(() => {
    if (!mqttMessage) return;

    try {
      const msg = JSON.parse(mqttMessage);
      //console.log('******Parsed message:', msg);

      const mac = msg.mac ?? msg.m;
      const operation = msg.operation ?? msg.o;

      // 축약형 센서 타입 → 풀네임 보정
      if (msg.typeSensor === 'humi') msg.typeSensor = 'humidity';
      else if (msg.typeSensor === 'temp') msg.typeSensor = 'temperature';

      /*
      console.log('operation:', operation);
      console.log('mac:', mac);
      console.log('deviceMac:', deviceMac);
      console.log('msg.typeSensor:', msg.typeSensor);
      console.log('typeSensor:', typeSensor);
      console.log('triggerValue:', msg.triggerValue);
      */

      if (operation !== 'list' || mac !== deviceMac) return;
      if (msg.typeSensor && msg.typeSensor !== typeSensor) return;

      const next: LogRow[] = [];
      const push = (r: LogRow) => {
        // 동일 slotIndex + trigger는 중복 방지
        if (!next.some(x => x.slotIndex === r.slotIndex && x.trigger === r.trigger)) next.push(r);
      };

      // 신구 포맷/필드명 수용
      const rawList = (msg.items ?? msg.list ?? msg.sl);
      const list = Array.isArray(rawList)
        ? rawList
        : (msg.trigger !== undefined || msg.tr !== undefined || msg.portState ? [msg] : []);

      list.forEach((it: any) => {
        // trigger: 'up'/'down' | true/false | 1/0
        const trigRaw = (it.trigger ?? it.tr);
        const trig: 'up' | 'down' | undefined =
          (trigRaw === 'up' || trigRaw === true || trigRaw === 1) ? 'up' :
            (trigRaw === 'down' || trigRaw === false || trigRaw === 0) ? 'down' :
              undefined;

        // portState 첫 항목만 사용(요구사항 기준)
        const psArr = Array.isArray(it.portState) ? it.portState : undefined;
        const ps0 = psArr ? psArr[0] : undefined;

        const slotIndex = (typeof it.slotIndex === 'number')
          ? it.slotIndex
          : Date.now() + Math.floor(Math.random() * 1000);

        //console.log('trigger:', trig);
        //console.log('portState:', ps0);

        if (trig && ps0) {
          const triggerValue = it.triggerValue ?? it.tv;
          /*console.log('Adding to rows:', {
            slotIndex,
            trigger: trig,
            typeSensor: it.ts,
            triggerValue,
            mac: ps0.mac ?? ps0.m,
            portNo: ps0.portNo ?? ps0.n,
            value: !!(ps0.value ?? ps0.v),
          });*/

          push({
            slotIndex,
            trigger: trig,
            typeSensor: it.ts, // 예: 'humidity', 'temperature'
            triggerValue,
            duration: typeof it.duration === 'number' ? it.duration : undefined,
            delay: it.delay ?? it.du ?? 0,
            mac: ps0.mac ?? ps0.m,
            portNo: ps0.portNo ?? ps0.n,
            value: !!(ps0.value ?? ps0.v),
          });
        }
      });

      // 구포맷(상/하 합본) 분리 처리
      if ((msg.sensorHighValue !== undefined || msg.sensorLowValue !== undefined) && Array.isArray(msg.portState)) {
        const d = typeof msg.duration === 'number' ? msg.duration : undefined;
        if (msg.portState[0] && typeof msg.sensorHighValue === 'number') {
          const ps = msg.portState[0];
          push({
            slotIndex: (typeof msg.slotIndex === 'number' ? msg.slotIndex : Date.now()) + 1,
            trigger: 'up',
            typeSensor: msg.ts,
            duration: d,
            mac: ps.mac ?? ps.m,
            portNo: ps.portNo ?? ps.n,
            value: !!(ps.value ?? ps.v),
          });
        }
        if (msg.portState[1] && typeof msg.sensorLowValue === 'number') {
          const ps = msg.portState[1];
          push({
            slotIndex: (typeof msg.slotIndex === 'number' ? msg.slotIndex : Date.now()) + 2,
            trigger: 'down',
            typeSensor: msg.ts,
            duration: d,
            mac: ps.mac ?? ps.m,
            portNo: ps.portNo ?? ps.n,
            value: !!(ps.value ?? ps.v),
          });
        }
      }

      // ✅ 기존 rows와 병합 + 중복 제거(누적 표시)
      //console.log('Updated rows (merge with prev):', next);
      setRows(prev => {
        const key = (r: LogRow) => `${r.trigger}-${r.slotIndex}`;
        const map = new Map<string, LogRow>();

        // 1) 기존 것 보존
        for (const r of prev) map.set(key(r), r);

        // 2) 새로 온 것 덮어쓰기(업데이트)
        for (const r of next) map.set(key(r), r);

        // 3) 정렬해서 반환
        return Array.from(map.values()).sort((a, b) => a.slotIndex - b.slotIndex);
      });

    } catch (e) {
      console.error('LIST parse error:', e);
    }
  }, [mqttMessage, deviceMac, typeSensor]);


  // ===== 동작 =====
  const handleList = () => {
    //setRows([]);
    publish({ command: 'bindSensor', typeSensor, mac: deviceMac, operation: 'list' });
  };

  // ✅ 선택된 트리거 하나만 insert
  const handleInsert = () => {
    if (!selectedTrigger) {
      alert('트리거(⬆️/⬇️) 를 선택하세요.');
      return;
    }

    if (selectedTrigger === 'up') {
      if (thresholdUp === '' || !upDeviceMac || upPort === null || upState === null) {
        alert('상승(올라갈 때) 항목을 모두 입력하세요.');
        return;
      }
      publish({
        command: 'bindSensor',
        typeSensor,
        mac: deviceMac,
        operation: 'insert',
        trigger: true,          // ⬆️ boolean 기록
        //tr: 1,                  // ⬆️ 압축값 기록
        triggerValue: Number(thresholdUp),        // ✅ 추가: 트리거 값
        duration: durationUp === '' ? 0 : Number(durationUp),
        delay: delayUp === '' ? 0 : Number(delayUp),
        portState: [{ mac: upDeviceMac, portNo: upPort, value: upState }],
      });
    }

    if (selectedTrigger === 'down') {
      if (thresholdDown === '' || !downDeviceMac || downPort === null || downState === null) {
        alert('하강(내려갈 때) 항목을 모두 입력하세요.');
        return;
      }
      publish({
        command: 'bindSensor',
        typeSensor,
        mac: deviceMac,
        operation: 'insert',
        trigger: false,         // ⬇️ boolean 기록
        //tr: 0,                  // ⬇️ 압축값 기록
        triggerValue: Number(thresholdDown),      // ✅ 추가: 트리거 값
        duration: durationDown === '' ? 0 : Number(durationDown),
        delay: delayDown === '' ? 0 : Number(delayDown),
        portState: [{ mac: downDeviceMac, portNo: downPort, value: downState }],
      });
    }

    setTimeout(handleList, 700);
  };

  const handleDelete = (slotIndex: number) => {
    publish({ command: 'bindSensor', typeSensor, mac: deviceMac, operation: 'delete', slotIndex });
    setRows(prev => prev.filter(r => r.slotIndex !== slotIndex));
    setTimeout(handleList, 500);
  };

  const handleDeleteAll = () => {
    publish({ command: 'bindSensor', typeSensor, mac: deviceMac, operation: 'deleteAll' });
    setRows([]);
    resetInputs();
    setTimeout(handleList, 500);
  };

  const handleSaveCalibration = () => {
    if (calSensor === '') {
      alert('보정값을 입력하세요.');
      return;
    }
    publish({ command: 'bindSensor', typeSensor, operation: 'calibration', mac: deviceMac, value: Number(calSensor) });
  };

  // ===== 유틸: 포트 라벨 계산 =====
  const getPortLabel = (rec: DeviceRecord, outIndex: number) => {
    const inCount = rec.in?.length || 0;
    const idxInName = 1 + inCount + outIndex; // name[0]은 장치명
    return rec.name?.[idxInName] || `스위치 ${outIndex + 1}`;
  };

  // ===== 렌더 =====
  return (
    <div className="modal-overlay">
      <div className="modal">
        <h3>{label} 센서 트리거</h3>

        {/* 리스트 (Table Style) */}
        <h4>설정된 트리거 목록</h4>
        <div className="modal-table-container">
          <table className="modal-table">
            <thead>
              <tr>
                <th>번호</th>
                <th>조건</th>
                <th>설정</th>
                <th>지연</th>
                <th>동작시간</th>
                <th>동작</th>
                <th>삭제</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="empty-message">등록된 트리거가 없습니다.</td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={`${r.trigger}-${r.slotIndex}`}>
                    <td>{r.slotIndex}</td>
                    <td>{r.trigger === 'up' ? '⬆️ 올라갈 때' : '⬇️ 내려갈 때'}</td>
                    <td>{r.triggerValue !== undefined ? `${r.triggerValue}` : ''}</td>
                    <td>{r.delay !== undefined && r.delay > 0 ? `${r.delay}s` : '-'}</td>
                    <td>{typeof r.duration === 'number' && r.duration > 0 ? `${r.duration}s` : '계속'}</td>
                    <td>{r.mac}:{r.portNo} {r.value ? 'ON' : 'OFF'}</td>
                    <td style={{ textAlign: 'center' }}>
                      <button className="btn-delete-icon" onClick={() => handleDelete(r.slotIndex)} title="삭제">
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* ✅ 트리거 선택 */}
        <h4>트리거 추가</h4>
        <div className="form-group">
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>트리거 선택</label>
            <select
              value={selectedTrigger}
              onChange={(e) => setSelectedTrigger(e.target.value as TriggerKind)}
            >
              <option value="">선택하세요</option>
              <option value="up">⬆️ 올라갈 때</option>
              <option value="down">⬇️ 내려갈 때</option>
            </select>
          </div>

          {/* 선택된 트리거만 입력 폼 표시 */}
          {selectedTrigger === 'up' && (
            <div style={{ marginTop: '15px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>상한 설정</label>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                <input
                  type="number"
                  value={thresholdUp}
                  placeholder="상한(임계값)"
                  onChange={(e) => setThresholdUp(e.target.value === '' ? '' : Number(e.target.value))}
                />
                <input
                  type="number"
                  value={delayUp}
                  placeholder="지연(초)"
                  onChange={(e) => setDelayUp(e.target.value === '' ? '' : Number(e.target.value))}
                />
                <input
                  type="number"
                  value={durationUp}
                  placeholder="동작시간(초, 0=무한)"
                  onChange={(e) => setDurationUp(e.target.value === '' ? '' : Number(e.target.value))}
                />
              </div>

              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>동작할 기기 및 포트</label>
              <select value={upDeviceMac} onChange={(e) => { setUpDeviceMac(e.target.value); setUpPort(null); setUpState(null); }} style={{ marginBottom: '10px' }}>
                <option value="">기기를 선택하세요</option>
                {records.map((r, i) => (
                  <option key={i} value={r.mac}>{r.name?.[0] || r.mac}</option>
                ))}
              </select>

              {upDeviceMac && (
                <select value={upPort ?? ''} onChange={(e) => setUpPort(Number(e.target.value))} style={{ marginBottom: '10px' }}>
                  <option value="" disabled>포트를 선택하세요</option>
                  {(records.find((r) => r.mac === upDeviceMac)?.out || []).map((_, idx) => {
                    const rec = records.find((r) => r.mac === upDeviceMac)!;
                    return (
                      <option key={idx} value={idx}>
                        {getPortLabel(rec, idx)}
                      </option>
                    );
                  })}
                </select>
              )}

              {upPort !== null && (
                <select value={upState !== null ? String(upState) : ''} onChange={(e) => setUpState(e.target.value === 'true')}>
                  <option value="" disabled>상태 선택</option>
                  <option value="true">On</option>
                  <option value="false">Off</option>
                </select>
              )}
            </div>
          )}

          {selectedTrigger === 'down' && (
            <div style={{ marginTop: '15px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>하한 설정</label>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                <input
                  type="number"
                  value={thresholdDown}
                  placeholder="하한(임계값)"
                  onChange={(e) => setThresholdDown(e.target.value === '' ? '' : Number(e.target.value))}
                />
                <input
                  type="number"
                  value={delayDown}
                  placeholder="지연(초)"
                  onChange={(e) => setDelayDown(e.target.value === '' ? '' : Number(e.target.value))}
                />
                <input
                  type="number"
                  value={durationDown}
                  placeholder="동작시간(초, 0=무한)"
                  onChange={(e) => setDurationDown(e.target.value === '' ? '' : Number(e.target.value))}
                />
              </div>

              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>동작할 기기 및 포트</label>
              <select value={downDeviceMac} onChange={(e) => { setDownDeviceMac(e.target.value); setDownPort(null); setDownState(null); }} style={{ marginBottom: '10px' }}>
                <option value="">기기를 선택하세요</option>
                {records.map((r, i) => (
                  <option key={i} value={r.mac}>{r.name?.[0] || r.mac}</option>
                ))}
              </select>

              {downDeviceMac && (
                <select value={downPort ?? ''} onChange={(e) => setDownPort(Number(e.target.value))} style={{ marginBottom: '10px' }}>
                  <option value="" disabled>포트를 선택하세요</option>
                  {(records.find((r) => r.mac === downDeviceMac)?.out || []).map((_, idx) => {
                    const rec = records.find((r) => r.mac === downDeviceMac)!;
                    return (
                      <option key={idx} value={idx}>
                        {getPortLabel(rec, idx)}
                      </option>
                    );
                  })}
                </select>
              )}

              {downPort !== null && (
                <select value={downState !== null ? String(downState) : ''} onChange={(e) => setDownState(e.target.value === 'true')}>
                  <option value="" disabled>상태 선택</option>
                  <option value="true">On</option>
                  <option value="false">Off</option>
                </select>
              )}
            </div>
          )}
        </div>

        {/* 센서 보정 */}
        <div className="form-group" style={{ marginTop: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>{label} 보정 (현재 입력)</label>
          <div style={{ display: 'flex', gap: '10px' }}>
            <input
              id="calSensor"
              type="number"
              value={calSensor}
              placeholder="예: 28.1"
              onChange={(e) => setCalSensor(e.target.value === '' ? '' : Number(e.target.value))}
            />
            <button className="modal-btn btn-view" onClick={handleSaveCalibration} style={{ whiteSpace: 'nowrap' }}>
              보정 저장
            </button>
          </div>
        </div>

        {/* 액션 버튼 */}
        <div className="modal-actions">
          <button className="modal-btn btn-view" onClick={handleList}>보기</button>
          <button className="modal-btn btn-save" onClick={handleInsert}>등록</button>
          <button className="modal-btn btn-delete-all" onClick={handleDeleteAll}>모두삭제</button>
          <button className="modal-btn btn-cancel" onClick={onCancel}>취소</button>
        </div>
      </div>
    </div>
  );
};

export default ModalMeasure;
